import classnames from 'classnames';
import React, { useContext } from 'react';
import { useDispatch } from 'react-redux';

import { listerClickThrough } from '../../../actions/lister-actions';
import { useEventListener } from '../../../hooks/useEventListener';
import { useIsProductInHighDemand } from '../../../hooks/useIsProductInHighDemand';
import TRANSLATIONS from '../../../translations/translations@default.json';
import {
	getDeliveryDate,
	getDeliveryDaysDifference,
	isBrowserIE11,
} from '../../../utilities/lister-utilities';
import { AcceptedRegions } from '../../../utilities/locale-utilities';
import { currencyFormatter } from '../../../utilities/number-utilities';
import { Breakpoint } from '../../../utilities/ui-utilities';
import { Context } from '../../Views/store/store';
import AddToBasketButton from '../AddToBasket/AddToBasketButton';
import DealsMobileSkeleton from '../DealsMobileProductCard/DealsMobileSkeleton';
import EnergyInformation from '../EnergyInformation/EnergyInformation';
import FinanceProposition from '../FinanceProposition';
import Heading from '../Heading';
import HighDemandFlag from '../HighDemand/HighDemandFlag';
import HighDemandMessaging from '../HighDemand/HighDemandMessaging';
import MobileDeliveryMessageBlock from '../MobileProductCard/MobileDeliveryMessage/MobileDeliveryMessageBlock@default';
import MoreInfo from '../MoreInfo';
import OutOfStock from '../OutOfStock/OutOfStock';
import PricePod from '../PricePod/PricePod@default';
import ProductImage from '../ProductImage/ProductImage';
import ReviewRating from '../ReviewRating/ReviewRating';
import DealsGridViewSkeleton from './DealsGridViewSkeleton';

const formatter = currencyFormatter(TRANSLATIONS.general);
const isIE11 = isBrowserIE11();

export const DealsGridViewProductCard = ({
	index,
	product,
	featuredProducts,
}) => {
	const [contextState, contextDispatch] = useContext(Context);
	const { loaded } = contextState;

	useEventListener(`lazy-load-${product.ColourId}`, () => {
		contextDispatch({ type: 'UPDATE_LOADED', payload: true });
	});

	const dispatch = useDispatch();

	const isSkuHighDemand = useIsProductInHighDemand(product);

	if (!loaded) {
		return (
			<React.Fragment>
				<Breakpoint.BelowMedium>
					<DealsGridViewSkeleton
						product={product}
						featuredProducts={featuredProducts}
					/>
				</Breakpoint.BelowMedium>
				<Breakpoint.MediumAndAbove>
					<DealsMobileSkeleton
						product={product}
						index={index}
						featuredProducts={featuredProducts}
					/>
				</Breakpoint.MediumAndAbove>
			</React.Fragment>
		);
	}

	const deliveryDate = getDeliveryDate(
		product.DynamicDeliveryBulletsViewModel[0]
	);

	const deliveryDaysDifference = getDeliveryDaysDifference(deliveryDate);

	const was =
		product.PricePodViewModel.WasPrice ||
		product.PricePodViewModel.RetailPriceIncVat;

	const now = product.PricePodViewModel.NowPrice;

	let saving = !isNaN(was) && !isNaN(now) ? Math.floor(was - now) : 0;

	return (
		<li
			className={classnames('p-1 md:p-2 relative', {
				'w-1/2': featuredProducts !== 1,
				'w-full': featuredProducts === 1,
			})}
			id={product.Code}>
			<a
				href={product.FullProductUrl}
				onClick={(event) => {
					event.preventDefault();
					window.location.assign(product.FullProductUrl);

					dispatch(listerClickThrough(product.Code));
				}}
				className="deals-product__link absolute inset-0 z-10"
				data-testid="product-card-link"
				data-tag-name="lister product card"
				data-tag-type={product.IsFeaturedProduct ? 'featured' : 'standard'}
				data-tag-delivery-date={deliveryDate}
				data-tag-delivery-days={deliveryDaysDifference}>
				<span className="block">
					{TRANSLATIONS.productList.productCard.viewProductAccessibilityText}
				</span>
			</a>

			<div className="deals-product relative flex flex-col bg-white rounded-lg border border-solid border-gray-30 p-4 h-full">
				{(product.State === 'OutOfStock' || isSkuHighDemand) && (
					<div className="flex pb-2">
						{isSkuHighDemand && product.State !== 'OutOfStock' && (
							<HighDemandFlag />
						)}
						{product.State === 'OutOfStock' && <OutOfStock deals />}
					</div>
				)}
				{!isSkuHighDemand && product.State !== 'OutOfStock' && (
					<div className="flex pb-2 justify-between">
						<div className="deals-promo-title flex justify-center items-center whitespace-no-wrap">
							<div className="bg-error flex rounded-xs h-10 items-center justify-center leading-none mr-2 p-3 w-full text-title-sm text-error">
								{saving > 0 ? (
									<React.Fragment>
										{TRANSLATIONS.productList.productCard.deals.save}
										<span className="ml-1 text-error text-title">
											{formatter.format(saving)}
										</span>
									</React.Fragment>
								) : (
									TRANSLATIONS.productList.productCard.deals.dealsFlag
								)}
							</div>
						</div>
						{/* <Breakpoint.MediumAndAbove>
								<div className="flex items-center">
									<PromoFlag
										{...product}
									/>
								</div>
							</Breakpoint.MediumAndAbove> */}
					</div>
				)}
				<div className="flex flex-col lg:flex-row h-full">
					<div
						className={classnames(
							'deals-product__image-side lg:flex lg:flex-col lg:justify-center lg:w-1/2 lg:mr-2',
							{
								'flex flex-col': isIE11,
							}
						)}
						style={isIE11 ? { minHeight: '1px' } : {}}>
						<ProductImage {...product} />

						{/* <Breakpoint.BelowMedium>
								<div className="flex items-center">
									<PromoFlag
										{...product}
									/>
								</div>
							</Breakpoint.BelowMedium> */}

						<Breakpoint.MediumAndAbove>
							{product.EnergyRating && (
								<div className="py-2 flex justify-center">
									<EnergyInformation
										energyRatingDetails={product.EnergyRatingDetails}
									/>
								</div>
							)}
							{!isSkuHighDemand && (
								<MobileDeliveryMessageBlock
									deliveryMessage={product.DynamicDeliveryBulletsViewModel[0]}
									stockStatus={product.State}
									deals
								/>
							)}
						</Breakpoint.MediumAndAbove>
					</div>
					<div className="deals-product__title-side flex flex-col justify-between w-full h-full lg:w-1/2 pt-2 lg:pt-0">
						<div>
							<Heading content={product.FullTitle} />
							<PricePod
								viewModel={product.PricePodViewModel}
								inStock={product.State !== 'OutOfStock'}
								{...product}
							/>
							<ReviewRating {...product} />
							<AcceptedRegions AOL>
								<FinanceProposition {...product} />
							</AcceptedRegions>
							<Breakpoint.BelowMedium>
								{product.EnergyRating && (
									<div className="pt-2 flex">
										<EnergyInformation
											energyRatingDetails={product.EnergyRatingDetails}
										/>
									</div>
								)}
							</Breakpoint.BelowMedium>
						</div>

						<div className="pt-2">
							<Breakpoint.BelowMedium>
								{!isSkuHighDemand && (
									<div className="pt-2">
										<MobileDeliveryMessageBlock
											deliveryMessage={
												product.DynamicDeliveryBulletsViewModel[0]
											}
											stockStatus={product.State}
											deals
										/>
									</div>
								)}
							</Breakpoint.BelowMedium>
							<MoreInfo {...product} />
							{product.State !== 'OutOfStock' && !isSkuHighDemand && (
								<AddToBasketButton {...product} />
							)}
						</div>
					</div>
				</div>

				{isSkuHighDemand && product.State !== 'OutOfStock' && (
					<div className="mt-1">
						<HighDemandMessaging />
					</div>
				)}
			</div>
		</li>
	);
};

export default DealsGridViewProductCard;
